import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView } from 'react-native';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SkipNextIcon from '@mui/icons-material/SkipNext'; import { FencingSwordsIcon, FencingMaskIcon } from '../FencingIcons';

/**
 * BoutSummaryModal - Popup che mostra il riepilogo dell'assalto appena concluso
 * 
 * Props:
 * - visible: boolean - se mostrare il modal
 * - onClose: () => void - callback per chiudere (al tap)
 * - boutNumber: number - numero dell'assalto appena concluso (1-based)
 * - fencerA: string - nome tiratore squadra A dell'assalto appena concluso
 * - fencerB: string - nome tiratore squadra B dell'assalto appena concluso
 * - pointsA: number - punti fatti dal tiratore A in questo assalto
 * - pointsB: number - punti fatti dal tiratore B in questo assalto
 * - nextFencerA: string - nome prossimo tiratore squadra A (opzionale)
 * - nextFencerB: string - nome prossimo tiratore squadra B (opzionale)
 * - totalScoreA: number - punteggio totale squadra A
 * - totalScoreB: number - punteggio totale squadra B
 */
const BoutSummaryModal = ({
    visible = false,
    onClose,
    boutNumber = 1,
    fencerA = '',
    fencerB = '',
    pointsA = 0,
    pointsB = 0,
    nextFencerA = null,
    nextFencerB = null,
    totalScoreA = 0,
    totalScoreB = 0,
    matchStats = null,
    isFinal = false
}) => {
    if (!visible) return null;

    const statsA = matchStats?.A || {};
    const statsB = matchStats?.B || {};
    const hasStats = Object.keys(statsA).length > 0 || Object.keys(statsB).length > 0;

    return (
        <TouchableOpacity
            style={styles.overlay}
            activeOpacity={1}
            onPress={onClose}
        >
            <View style={styles.card}>
                <ScrollView contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
                    {/* Header con icona e titolo */}
                    <View style={styles.header}>
                        {isFinal ? (
                            <EmojiEventsIcon style={{ fontSize: 32, color: '#fbc02d', marginRight: 12 }} />
                        ) : (
                            <FencingSwordsIcon style={{ fontSize: 32, color: '#1976d2', marginRight: 12 }} />
                        )}
                        <Text style={styles.title}>
                            {isFinal ? 'Incontro Terminato!' : `Assalto ${boutNumber} Terminato!`}
                        </Text>
                    </View>

                    {/* Separatore decorativo */}
                    <View style={styles.divider} />

                    {/* Punteggio dell'assalto */}
                    <View style={styles.scoreSection}>
                        <View style={styles.fencerColumn}>
                            <Text style={styles.fencerName} numberOfLines={1}>{fencerA || '—'}</Text>
                            <View style={[styles.pointsBadge, styles.pointsBadgeA]}>
                                <Text style={styles.pointsText}>+{pointsA}</Text>
                            </View>
                        </View>

                        <View style={styles.vsContainer}>
                            <Text style={styles.vsText}>VS</Text>
                        </View>

                        <View style={styles.fencerColumn}>
                            <Text style={styles.fencerName} numberOfLines={1}>{fencerB || '—'}</Text>
                            <View style={[styles.pointsBadge, styles.pointsBadgeB]}>
                                <Text style={styles.pointsText}>+{pointsB}</Text>
                            </View>
                        </View>
                    </View>

                    {/* Punteggio totale */}
                    <View style={styles.totalSection}>
                        <Text style={styles.totalLabel}>Punteggio {isFinal ? 'Finale' : 'Totale'}</Text>
                        <View style={styles.totalScoreRow}>
                            <Text style={[styles.totalScore, styles.scoreA]}>{totalScoreA}</Text>
                            <Text style={styles.totalDash}>-</Text>
                            <Text style={[styles.totalScore, styles.scoreB]}>{totalScoreB}</Text>
                        </View>
                    </View>

                    {/* Statistiche Individuali (solo se presenti) */}
                    {hasStats && (
                        <>
                            <View style={styles.divider} />
                            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                                <FencingMaskIcon style={{ fontSize: 20, color: '#1976d2', marginRight: 8 }} />
                                <Text style={styles.statsHeader}>Statistiche Punti</Text>
                            </View>
                            <View style={styles.statsRow}>
                                {/* Colonna Squadra A */}
                                <View style={styles.statsColumn}>
                                    {Object.entries(statsA).map(([name, pts]) => (
                                        <View key={name} style={styles.statLine}>
                                            <Text style={styles.statName} numberOfLines={1}>{name}</Text>
                                            <Text style={[styles.statValue, styles.scoreA]}>{pts}</Text>
                                        </View>
                                    ))}
                                </View>

                                <View style={styles.statsDivider} />

                                {/* Colonna Squadra B */}
                                <View style={styles.statsColumn}>
                                    {Object.entries(statsB).map(([name, pts]) => (
                                        <View key={name} style={styles.statLine}>
                                            <Text style={styles.statName} numberOfLines={1}>{name}</Text>
                                            <Text style={[styles.statValue, styles.scoreB]}>{pts}</Text>
                                        </View>
                                    ))}
                                </View>
                            </View>
                        </>
                    )}

                    {/* Prossimi tiratori (solo se ci sono e non è finale) */}
                    {!isFinal && (nextFencerA || nextFencerB) && (
                        <>
                            <View style={styles.divider} />
                            <View style={styles.nextSection}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
                                    <SkipNextIcon style={{ fontSize: 20, color: '#1976d2', marginRight: 8 }} />
                                    <Text style={styles.nextLabel}>Prossimo Assalto</Text>
                                </View>
                                <View style={styles.nextFencersRow}>
                                    <Text style={styles.nextFencer} numberOfLines={1}>{nextFencerA || '—'}</Text>
                                    <Text style={styles.nextVs}>vs</Text>
                                    <Text style={styles.nextFencer} numberOfLines={1}>{nextFencerB || '—'}</Text>
                                </View>
                            </View>
                        </>
                    )}

                    {/* Hint per chiudere */}
                    <Text style={styles.tapHint}>Tocca per {isFinal ? 'chiudere' : 'continuare'}</Text>
                </ScrollView>
            </View>
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start', // Spostato in alto
        paddingHorizontal: 20,
        paddingTop: 40, // Spazio dal bordo superiore
        paddingBottom: 20,
    },
    card: {
        backgroundColor: '#ffffff',
        borderRadius: 20,
        padding: 20,
        width: '95%',
        maxWidth: 500,
        maxHeight: '85%',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 10 },
        shadowOpacity: 0.3,
        shadowRadius: 20,
        elevation: 15,
        overflow: 'hidden', // Importante per borderRadius con ScrollView
    },
    scrollContent: {
        paddingBottom: 10,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 12,
    },
    icon: {
        fontSize: 28,
        marginRight: 10,
    },
    title: {
        fontSize: 18, // Ridotto da 22
        fontWeight: '700',
        color: '#1976d2',
        letterSpacing: 0.5,
    },
    divider: {
        height: 2,
        backgroundColor: '#e0e0e0',
        marginVertical: 12, // Ridotto da 16
        borderRadius: 1,
    },
    scoreSection: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 20,
    },
    fencerColumn: {
        flex: 1,
        alignItems: 'center',
    },
    fencerName: {
        fontSize: 15,
        fontWeight: '600',
        color: '#333',
        marginBottom: 8,
        textAlign: 'center',
        maxWidth: 100,
    },
    pointsBadge: {
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 20,
        minWidth: 60,
        alignItems: 'center',
    },
    pointsBadgeA: {
        backgroundColor: '#ffebee',
        borderWidth: 2,
        borderColor: '#d32f2f',
    },
    pointsBadgeB: {
        backgroundColor: '#e8f5e9',
        borderWidth: 2,
        borderColor: '#388e3c',
    },
    pointsText: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#333',
    },
    vsContainer: {
        paddingHorizontal: 12,
    },
    vsText: {
        fontSize: 14,
        fontWeight: '600',
        color: '#999',
    },
    totalSection: {
        alignItems: 'center',
        marginBottom: 8,
    },
    totalLabel: {
        fontSize: 13,
        color: '#666',
        marginBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: 1,
    },
    totalScoreRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    totalScore: {
        fontSize: 32,
        fontWeight: 'bold',
    },
    scoreA: {
        color: '#d32f2f',
    },
    scoreB: {
        color: '#388e3c',
    },
    totalDash: {
        fontSize: 28,
        fontWeight: '300',
        color: '#999',
        marginHorizontal: 12,
    },
    nextSection: {
        alignItems: 'center',
    },
    nextLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: '#1976d2',
        marginBottom: 10,
    },
    nextFencersRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
    },
    nextFencer: {
        fontSize: 16,
        fontWeight: '600',
        color: '#333',
        maxWidth: 120,
        textAlign: 'center',
    },
    nextVs: {
        fontSize: 12,
        color: '#999',
        marginHorizontal: 12,
    },
    tapHint: {
        marginTop: 20,
        fontSize: 12,
        color: '#aaa',
        textAlign: 'center',
        fontStyle: 'italic',
    },
    statsHeader: {
        fontSize: 15,
        fontWeight: '700',
        color: '#1976d2',
        marginBottom: 12,
        textAlign: 'center',
    },
    statsRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    statsColumn: {
        flex: 1,
    },
    statsDivider: {
        width: 1,
        backgroundColor: '#eee',
        marginHorizontal: 12,
    },
    statLine: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
        backgroundColor: '#fafafa',
        padding: 6,
        borderRadius: 6,
    },
    statName: {
        fontSize: 12,
        color: '#333',
        fontWeight: '500',
        flex: 1,
        marginRight: 4,
    },
    statValue: {
        fontSize: 14,
        fontWeight: 'bold',
    },
});

export default BoutSummaryModal;
